import { Component } from '../../decorators/component.decorator';
import { UIElement } from '../ui-element';
import styles from './ui-slideview.css';
import { TranslationLabels } from '../../types/labels.type';
import { Labels } from '../../global/labels';
import { Child } from '../../decorators/child.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { UISlideViewDetail } from './ui-slideview-detail';
import { TabIndex } from '../../global/keyboard';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-slideview
 * @augments {UIElement}
 * @alias UISlideView
 * @classdesc Represents a class for <code>ui-slideview</code> element.
 * @property {string} [backLabel] {@attr back-label} Accessible label for the back control.
 * @property {boolean} [resetOnClose] {@attr reset-on-close} When a host ui-modal closes, set this property to instantly return to the
 *   main view on the next open. Useful for type="menu" overlays so reopen always starts on main.
 * @slot
 * @example
 * <ui-slideview>
 *    <ui-slideview-main>
 *      <ui-assets>
 *        <ui-asset target="view-1" label="Category 1"></ui-asset>
 *        <ui-asset target="view-2" label="Category 2"></ui-asset>
 *      </ui-assets>
 *    </ui-slideview-main>
 *    <ui-slideview-detail id="view-1">
 *      <h3>Detail 1</h3>
 *      <p>Lorem ipsum dolor sit amet, consectetur adipisicing elit.</p>
 *    </ui-slideview-detail>
 *    <ui-slideview-detail id="view-2">
 *      <h3>Detail 2</h3>
 *      <p>Lorem ipsum dolor sit amet, consectetur adipisicing elit.</p>
 *    </ui-slideview-detail>
 * </ui-slideview>
 */
@Component({
    tag: 'ui-slideview',
    styles,
})
export class UISlideView extends UIElement {
    @Attribute() backLabel?: string;
    @Attribute(Boolean) resetOnClose?: boolean;
    @Child('.ui-slideview__viewport') viewport!: HTMLDivElement;
    @Child('.ui-slideview__slide') slideContainer!: HTMLDivElement;
    @Child('.ui-slideview__mains') mainContainer!: HTMLDivElement;
    @Child('.ui-slideview__details') detailContainer!: HTMLDivElement;
    @Child('.ui-slideview__back') backButton!: HTMLButtonElement;

    private resizeObserver: ResizeObserver | null = null;
    private pendingFrame: number | null = null;
    private onTransitionEnd: ((event: Event) => void) | null = null;

    /**
     * @type {boolean}
     */
    get isMainView(): boolean {
        return this.classList.contains('-is-main');
    }

    /**
     * @type {boolean}
     */
    get isDetailView(): boolean {
        return this.classList.contains('-is-detail');
    }

    /**
     * @type {UISlideViewDetail | null}
     */
    get activeDetail(): UISlideViewDetail | null {
        return this.detailContainer.querySelector('.-is-active');
    }

    /**
     * Parent modal when the slideview is hosted inside a ui-modal
     * @type {HTMLElement | null}
     * @private
     */
    private get parentModal(): HTMLElement | null {
        return this.closest('ui-modal');
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-slideview', {
            back: 'Back',
        });
    }

    /**
     * Opens a detail view by its ID.
     * @param {string} detailId - The ID of the detail view to open.
     */
    openDetail(detailId: string): void {
        const target = document.getElementById(detailId) as UISlideViewDetail;
        if (!target || !this.isMainView) {
            return;
        }

        this.applyView(target, { animate: true });
    }

    /**
     * Closes detail view, and returns to main view.
     */
    closeDetail(): void {
        if (!this.isDetailView) {
            return;
        }

        const activeId = this.activeDetail?.id;
        this.applyView(null, { animate: true });

        if (activeId) {
            this.mainContainer
                .querySelector<HTMLElement>(`[target="${activeId}"]`)
                ?.focus();
        }
    }

    /**
     * Cancels any in-flight slide frame or transitionend listener.
     * @private
     */
    private cancelPendingTransition(): void {
        if (this.pendingFrame !== null) {
            cancelAnimationFrame(this.pendingFrame);
            this.pendingFrame = null;
        }
        if (this.onTransitionEnd) {
            this.slideContainer?.removeEventListener(
                'transitionend',
                this.onTransitionEnd
            );
            this.onTransitionEnd = null;
        }
        this.classList.remove('-transition');
    }

    /**
     * Applies main vs detail mode classes, aria-hidden and modal back-action.
     * @private
     * @param {boolean} showDetail
     */
    private setViewMode(showDetail: boolean): void {
        this.classList.toggle('-is-main', !showDetail);
        this.classList.toggle('-is-detail', showDetail);

        if (showDetail) {
            this.mainContainer.setAttribute('aria-hidden', 'true');
            this.detailContainer.removeAttribute('aria-hidden');
        } else {
            this.mainContainer.removeAttribute('aria-hidden');
            this.detailContainer.setAttribute('aria-hidden', 'true');
        }

        this.syncModalBackAction(showDetail);
        this.syncViewportHeight();
    }

    /**
     * Marks detail as active or clears the active detail when null.
     * @private
     * @param {UISlideViewDetail | null} detail
     */
    private setActiveDetail(detail: UISlideViewDetail | null): void {
        this.activeDetail?.classList.remove('-is-active');
        detail?.classList.add('-is-active');
    }

    /**
     * Moves to a detail pane or back to main when detail is null.
     * @private
     * @param {UISlideViewDetail | null} detail
     * @param {{ animate: boolean }} options
     */
    private applyView(
        detail: UISlideViewDetail | null,
        { animate }: { animate: boolean }
    ): void {
        this.cancelPendingTransition();

        const showDetail = detail !== null;
        this.handleTabIndex(showDetail);

        if (!animate) {
            this.setActiveDetail(detail);
            this.setViewMode(showDetail);
            return;
        }

        this.classList.add('-transition');
        this.pendingFrame = requestAnimationFrame(() => {
            this.pendingFrame = null;

            if (detail) {
                this.setActiveDetail(detail);
                this.setViewMode(showDetail);
                this.focusBackControl();
            } else {
                this.setViewMode(showDetail);
            }

            const onEnd = (event: Event) => {
                if (
                    event instanceof TransitionEvent &&
                    event.propertyName !== 'transform' &&
                    event.propertyName !== 'height'
                ) {
                    return;
                }

                this.slideContainer.removeEventListener('transitionend', onEnd);
                if (this.onTransitionEnd === onEnd) {
                    this.onTransitionEnd = null;
                }
                this.classList.remove('-transition');

                if (!showDetail) {
                    this.setActiveDetail(null);
                }
                this.syncViewportHeight();
            };

            this.onTransitionEnd = onEnd;
            this.slideContainer.addEventListener('transitionend', onEnd);
        });
    }

    /**
     * Handles the tab index for focusable elements based on the current view.
     * @param {boolean} isDetailView - Indicates if the current view is the detail view.
     */
    handleTabIndex(isDetailView: boolean) {
        if (this.backButton && !this.parentModal) {
            if (isDetailView) {
                this.backButton.removeAttribute('tabindex');
            } else {
                this.backButton.setAttribute('tabindex', TabIndex.Inactive);
            }
        }
        this.handleMainFocusableElements(isDetailView);
    }

    /**
     * Handles the focusable elements in the main container.
     * @param {boolean} setTabIndex - Indicates whether to set or remove the tabindex.
     */
    handleMainFocusableElements(setTabIndex: boolean): void {
        const selector = setTabIndex
            ? 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
            : '[tabindex="-1"]';
        const tabIndexValue = setTabIndex ? TabIndex.Inactive : TabIndex.Active;

        this.mainContainer
            .querySelectorAll<HTMLElement>(selector)
            .forEach((element) => {
                element.setAttribute('tabindex', tabIndexValue);
            });
    }

    /**
     * Focuses the active back control (modal header button when hosted in a
     * modal, otherwise the slideview's own icon button).
     * @private
     */
    private focusBackControl(): void {
        const modalBack = this.parentModal?.querySelector<HTMLElement>(
            '.ui-modal__back-action'
        );
        if (modalBack) {
            modalBack.focus();
            return;
        }
        this.backButton?.focus();
    }

    /**
     * When hosted in a ui-modal, prefer the modal header back-action button
     * and hide the inline back button.
     * @private
     * @param {boolean} show
     */
    private syncModalBackAction(show: boolean): void {
        const modal = this.parentModal;
        if (!modal) {
            return;
        }

        if (show) {
            modal.setAttribute('back-action', '');
        } else {
            modal.removeAttribute('back-action');
        }
    }

    /**
     * Hides the inline back button when a parent modal owns back-action.
     * @private
     */
    private syncBackButtonHost(): void {
        if (!this.backButton) {
            return;
        }
        const useModalBack = Boolean(this.parentModal);
        this.backButton.classList.toggle('-hidden', useModalBack);
        this.backButton.toggleAttribute('aria-hidden', useModalBack);
        if (useModalBack) {
            this.backButton.setAttribute('tabindex', TabIndex.Inactive);
        }
        this.syncModalBackAction(useModalBack && this.isDetailView);
    }

    /**
     * Sizes the viewport to the active pane so the host (e.g. menu modal)
     * shrinks/ grows with content instead of staying at the parent pane's height.
     * @private
     */
    private syncViewportHeight(): void {
        if (!this.viewport || !this.mainContainer || !this.detailContainer) {
            return;
        }

        const active = this.isDetailView
            ? this.detailContainer
            : this.mainContainer;
        const height = active.offsetHeight;
        if (height <= 0) {
            return;
        }

        const next = `${height}px`;
        if (this.viewport.style.height !== next) {
            this.viewport.style.height = next;
            this.dispatchCustomEvent('popover-updated');
        }
    }

    /**
     * Creates an icon-only back button matching ui-modal's back-action control.
     * @private
     * @returns {HTMLElement} The created back button element.
     */
    private createBackButton(): HTMLElement {
        return this.createElement({
            tagName: 'ui-button',
            classList: {
                'ui-slideview__back': true,
            },
            attributes: {
                'type': 'button',
                'aria-label': this.backLabel || UISlideView.labels.back,
                'title': this.backLabel || UISlideView.labels.back,
                'layout': 'tertiary',
                'glyph': 'redesign-navigate-back',
            },
        });
    }

    /**
     * Wraps ui-slideview-main elements in a container div.
     * Handling preceding and trailing nodes to keep them on the 1st level.
     * @private
     */
    private wrapMainElements(): void {
        const mainElements = Array.from(
            this.querySelectorAll('ui-slideview-main')
        );
        const mainContainer = this.createElement({
            tagName: 'div',
            classList: {
                'ui-slideview__mains': true,
            },
        });

        const precedingNodes = this.getPrecedingNodes(mainElements[0]);
        const trailingNodes = this.getTrailingNodes(
            mainElements[mainElements.length - 1]
        );

        precedingNodes.forEach((node) => mainContainer.appendChild(node));
        mainElements.forEach((element) => mainContainer.appendChild(element));
        trailingNodes.forEach((node) => mainContainer.appendChild(node));

        this.insertBefore(mainContainer, this.firstElementChild);
    }

    /**
     * Gets all nodes preceding a specific element.
     * @private
     * @param {Element} targetElement - The element to find preceding nodes for.
     * @returns {Node[]} Array of nodes that precede the target element.
     */
    private getPrecedingNodes(targetElement: Element): Array<Node> {
        const precedingNodes: Array<Node> = [];
        let currentNode = this.firstChild;

        while (currentNode && currentNode !== targetElement) {
            const nextNode = currentNode.nextSibling;
            precedingNodes.push(currentNode);
            currentNode = nextNode;
        }

        return precedingNodes;
    }

    /**
     * Gets all nodes following a specific element until a ui-slideview-detail is encountered.
     * @private
     * @param {Element} targetElement - The element to find trailing nodes after.
     * @returns {Node[]} Array of nodes that follow the target element.
     */
    private getTrailingNodes(targetElement: Element): Array<Node> {
        const trailingNodes: Array<Node> = [];
        let currentNode = targetElement.nextSibling;

        while (currentNode) {
            // Stop if we encounter ui-slideview-detail element
            if (
                currentNode instanceof Element &&
                currentNode.tagName.toLowerCase() === 'ui-slideview-detail'
            ) {
                break;
            }
            const nextNode = currentNode.nextSibling;
            trailingNodes.push(currentNode);
            currentNode = nextNode;
        }

        return trailingNodes;
    }

    /**
     * Wraps ui-slideview-detail elements in a container div.
     * @private
     */
    private wrapDetailElements(): void {
        const detailElements = Array.from(
            this.querySelectorAll('ui-slideview-detail')
        );
        const detailContainer = this.createElement({
            tagName: 'div',
            classList: {
                'ui-slideview__details': true,
            },
        });

        detailElements.forEach((detail) => detailContainer.appendChild(detail));
        this.appendChild(detailContainer);
    }

    /**
     * Wraps main and detail view containers into divs for sliding animation.
     * @private
     */
    private wrapViewContainers(): void {
        if (this.slideContainer) {
            return;
        }
        const viewpoint = this.createElement({
            tagName: 'div',
            classList: {
                'ui-slideview__viewport': true,
            },
            children: [
                {
                    tagName: 'div',
                    classList: {
                        'ui-slideview__slide': true,
                    },
                    children: [this.mainContainer, this.detailContainer],
                },
            ],
        });
        this.appendChild(viewpoint);
    }

    /**
     * Handles the `slideview-open` event to open a specific detail view.
     * @param {Event} event
     * @private
     */
    private handleSlideViewOpen = (event: Event) => {
        const { view } = (event as CustomEvent<{ view: string }>).detail || {};
        if (view) {
            this.openDetail(view);
        }
    };

    /**
     * Handles the click event on the back button to close the detail view.
     * @private
     */
    private handleBackClick = () => {
        this.closeDetail();
    };

    /**
     * Handles ui-modal header back-action when the slideview is hosted in a modal.
     * @param {Event} event
     * @private
     */
    private handleModalBack = (event: Event) => {
        const target = event.target as HTMLElement;
        if (target?.contains(this) && this.isDetailView) {
            this.closeDetail();
        }
    };

    /**
     * When reset-on-close is set, snaps back to main as the host modal closes.
     * @param {Event} event
     * @private
     */
    private handleModalClose = (event: Event) => {
        if (!this.resetOnClose) {
            return;
        }
        const target = event.target as HTMLElement;
        if (target?.contains(this)) {
            this.applyView(null, { animate: false });
        }
    };

    /**
     * @private
     */
    private setupResizeObserver(): void {
        this.teardownResizeObserver();
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        this.resizeObserver = new ResizeObserver(() => {
            this.syncViewportHeight();
        });
        this.resizeObserver.observe(this.mainContainer);
        this.resizeObserver.observe(this.detailContainer);
    }

    /**
     * @private
     */
    private teardownResizeObserver(): void {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }

    /**
     * Adds event listeners for back control and slideview-open event.
     * @private
     */
    private addEventListeners() {
        this.backButton?.addEventListener('click', this.handleBackClick);
        this.addEventListener('slideview-open', this.handleSlideViewOpen);
        document.addEventListener('modal-back', this.handleModalBack);
        document.addEventListener('modal-close', this.handleModalClose);
    }

    /**
     * Removes event listeners for back control and slideview-open event.
     * @private
     */
    private removeEventListeners() {
        this.backButton?.removeEventListener('click', this.handleBackClick);
        this.removeEventListener('slideview-open', this.handleSlideViewOpen);
        document.removeEventListener('modal-back', this.handleModalBack);
        document.removeEventListener('modal-close', this.handleModalClose);
    }

    /**
     * @inheritDoc
     */
    render() {
        this.wrapMainElements();
        this.wrapDetailElements();

        this.detailContainer.prepend(this.createBackButton());

        this.mainContainer.setAttribute('role', 'group');
        this.detailContainer.setAttribute('aria-hidden', 'true');
        this.detailContainer.setAttribute('role', 'group');

        this.wrapViewContainers();
        if (!this.isMainView && !this.isDetailView) {
            this.classList.add('-is-main');
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.addEventListeners();
        this.syncBackButtonHost();
        this.setupResizeObserver();
        this.syncViewportHeight();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.removeEventListeners();
        this.cancelPendingTransition();
        this.teardownResizeObserver();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.addEventListeners();
        this.syncBackButtonHost();
        this.setupResizeObserver();
        this.syncViewportHeight();
    }
}
